import type { CalculatedMeasure, DatasetPreviewRequest, DatasetProfile } from 'src/components/star-grid/datasets/datasetTypes';
import { buildDatasetColumns as storedColumns, type DraftColumnChoices } from 'src/components/star-grid/datasets/utils/datasetDraft';

const REVIEW_SAMPLE_MONTHS = 3;
/** The service's cap on preview columns. */
const MAX_PREVIEW_COLUMNS = 96;

/** A sampled time value as `YYYYMM`, whether it was stored as `202601`, `'2026-01'` or a date. */
function sampleMonth(value: string): number | null {
  const digits = value.replace(/[^0-9]/g, '').slice(0, 6);
  const month = Number(digits.slice(4, 6));
  return digits.length === 6 && month >= 1 && month <= 12 ? Number(digits) : null;
}

/** The `count` calendar months ending at `last` (`YYYYMM`), oldest first. */
function monthsEndingAt(last: number, count: number): number[] {
  return Array.from({ length: count }, (_, back) => {
    const index = Math.floor(last / 100) * 12 + (last % 100) - 1 - back;
    return Math.floor(index / 12) * 100 + (index % 12) + 1;
  }).reverse();
}

/**
 * A draft dataset's Review query: every dimension as a row, and every measure for each of the three months ending at
 * the latest sampled month (and the first sampled scenario, when it has one). `null` when the profile names no
 * measure or no readable month, since the query cannot be built without both.
 */
export function reviewPreviewRequest(
  profile: DatasetProfile,
  choices: DraftColumnChoices,
  calculated: readonly CalculatedMeasure[] = []
): DatasetPreviewRequest | null {
  const columns = storedColumns(profile, choices, calculated);
  const sampled = (role: string): string[] => {
    const column = columns.find((entry) => entry.role === role);
    return profile.columns.find((entry) => entry.name === column?.name)?.samples ?? [];
  };
  const months = sampled('time')
    .map(sampleMonth)
    .filter((month): month is number => month !== null);
  const measures = columns.filter((column) => column.role === 'measure').map((column) => column.name);
  if (measures.length === 0 || months.length === 0) return null;
  const scenario = sampled('scenario')[0];
  const shown = monthsEndingAt(Math.max(...months), REVIEW_SAMPLE_MONTHS);
  return {
    rows: columns.filter((column) => column.role === 'dimension').map((column) => ({ column: column.name, members: [] })),
    filters: [],
    columns: measures
      .slice(0, Math.floor(MAX_PREVIEW_COLUMNS / REVIEW_SAMPLE_MONTHS))
      .flatMap((measure) => shown.map((month) => ({ ...(scenario ? { scenario } : {}), measure, months: [month] }))),
    measure: measures[0]
  };
}
